import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { getCompanyInfo } from '../../../../services/PublicBookingService';
import {
    generateClubCoupon, getClubCoupons, getClubMe, getClubShowcase,
    readSession, requestClubCode, verifyClubCode, writeSession
} from '../../../../services/PublicClubService';
import {
    clubImageUrl, facebookUrl, formatCouponCode, instagramUrl, profileHandle,
    usageLimitLabel, websiteUrl, whatsappUrl
} from '../../../../utils/ClubUtils';
import { getStoredCustomerDocument } from '../../../../utils/PublicBookingStorage';
import ClientTabBar from '../shared/ClientTabBar';
import PublicHero from '../shared/PublicHero';
import { getBrandVars } from '../shared/publicBrand';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import '../booking/PublicBooking.css';
import './Club.css';

const tokenKey = (company) => `club-token:${company}`;
const documentKey = (company) => `club-document:${company}`;

const COUPON_STATUS = {
    VALID: { label: 'Válido', tone: 'ok' },
    USED: { label: 'Usado', tone: 'muted' },
    EXPIRED: { label: 'Vencido', tone: 'muted' },
    CANCELED: { label: 'Cancelado', tone: 'muted' }
};

const formatDateTime = (value) => (value
    ? new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '');
const formatDate = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR') : '');

const apiMessage = (error, fallback) => error?.response?.data?.message || fallback;

/** Logo do parceiro, ou a inicial dele na cor da marca quando ainda não há arte. */
const PartnerAvatar = ({ partner, size = 56 }) => {
    const src = clubImageUrl(partner.logoPath);
    return src
        ? <img className="club-avatar" style={{ width: size, height: size }} src={src} alt="" />
        : <span className="club-avatar club-avatar--initial" style={{ width: size, height: size, background: partner.brandColor || 'var(--club-accent)' }}>
            {(partner.name || '?').charAt(0).toUpperCase()}
        </span>;
};

const PartnerCard = ({ partner, onOpen }) => {
    const cover = clubImageUrl(partner.coverPath);
    const visibleOffers = partner.offers.slice(0, 2);
    return (
        <article className="club-card" onClick={() => onOpen(partner)} role="button" tabIndex={0}
            onKeyDown={(event) => event.key === 'Enter' && onOpen(partner)}>
            <div className="club-card__cover" style={cover
                ? { backgroundImage: `url(${cover})` }
                : { background: `linear-gradient(135deg, ${partner.brandColor || '#fb6a21'}, color-mix(in srgb, ${partner.brandColor || '#fb6a21'} 55%, #000))` }}>
                {partner.featured && <span className="club-badge"><i className="fa-solid fa-star"></i> Destaque</span>}
            </div>
            <div className="club-card__body">
                <div className="club-card__logo"><PartnerAvatar partner={partner} size={60} /></div>
                <h3 className="club-card__name">{partner.name}</h3>
                {partner.category && <span className="club-pill">{partner.category}</span>}
                {partner.locality && <span className="club-card__locality"><i className="fa-solid fa-location-dot"></i> {partner.locality}</span>}
                {partner.description && <p className="club-card__desc">{partner.description}</p>}
                <ul className="club-card__offers">
                    {visibleOffers.length === 0
                        ? <li className="club-card__soon">Novidades em breve</li>
                        : visibleOffers.map((offer) => <li key={offer.id}><i className="fa-solid fa-ticket"></i> {offer.title}</li>)}
                    {partner.offers.length > visibleOffers.length && <li className="club-card__more">+{partner.offers.length - visibleOffers.length} {partner.offers.length - visibleOffers.length === 1 ? 'oferta' : 'ofertas'}</li>}
                </ul>
                <span className="club-card__cta">Ver detalhes <i className="fa-solid fa-arrow-right"></i></span>
            </div>
        </article>
    );
};

const ClubPage = () => {
    const { companyUuid } = useParams();

    const [showcase, setShowcase] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [category, setCategory] = useState('');

    const [token, setToken] = useState(() => {
        const bookingDocument = getStoredCustomerDocument(companyUuid);
        const verifiedDocument = readSession(documentKey(companyUuid));
        // Uma sessão confirmada para outro cliente não pode acompanhar a troca no booking.
        if (bookingDocument && verifiedDocument && bookingDocument !== verifiedDocument) {
            writeSession(tokenKey(companyUuid), '');
            return '';
        }
        return readSession(tokenKey(companyUuid));
    });
    const [me, setMe] = useState(null);

    const [selected, setSelected] = useState(null);
    const [coupon, setCoupon] = useState(null);
    const [couponsOpen, setCouponsOpen] = useState(false);
    const [coupons, setCoupons] = useState([]);
    const [authOpen, setAuthOpen] = useState(false);
    const [pendingOfferId, setPendingOfferId] = useState(null);
    const [pendingCoupons, setPendingCoupons] = useState(false);
    const [busyOffer, setBusyOffer] = useState(null);
    const [notice, setNotice] = useState('');

    useEffect(() => {
        getClubShowcase(companyUuid)
            .then(({ data }) => setShowcase(data))
            .catch((error) => setLoadError(apiMessage(error, 'Não foi possível abrir o Clube agora.')));
        getCompanyInfo(companyUuid).then(({ data }) => setCompanyInfo(data)).catch(() => setCompanyInfo(null));
    }, [companyUuid]);

    const logout = useCallback(() => {
        writeSession(tokenKey(companyUuid), '');
        setToken('');
        setMe(null);
    }, [companyUuid]);

    // Quem já confirmou o aparelho volta direto; sessão vencida ou sem direito limpa e pede de novo.
    useEffect(() => {
        if (!token) return;
        getClubMe(companyUuid, token)
            .then(({ data }) => {
                setMe(data);
                if (data.renewedToken) {
                    writeSession(tokenKey(companyUuid), data.renewedToken);
                    setToken(data.renewedToken);
                }
            })
            .catch((error) => {
                if (error?.response?.status === 401) logout();
                else if (error?.response?.status === 403) {
                    logout();
                    setNotice(apiMessage(error, 'O Clube é exclusivo para assinantes com plano ativo.'));
                }
            });
    }, [token, companyUuid, logout]);

    const accent = companyInfo?.headerColor || '#fb6a21';

    // Os modais são desenhados fora da página (no body) e não herdam a cor da marca do estilo
    // da página: a cor vai para o body enquanto o Clube está aberto.
    useEffect(() => {
        document.body.style.setProperty('--club-accent', accent);
        return () => document.body.style.removeProperty('--club-accent');
    }, [accent]);

    const partners = useMemo(() => (showcase?.partners || []).filter((partner) => !category || partner.category === category), [showcase, category]);
    const featured = useMemo(() => (showcase?.partners || []).filter((partner) => partner.featured && partner.coverPath), [showcase]);

    const redeem = (offerId, sessionToken = token) => {
        setBusyOffer(offerId);
        setNotice('');
        generateClubCoupon(companyUuid, sessionToken, offerId)
            .then(({ data }) => {
                // O detalhe do parceiro fecha: o cupom é a única coisa que importa agora.
                setSelected(null);
                setCoupon(data);
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    logout();
                    setPendingOfferId(offerId);
                    setAuthOpen(true);
                } else {
                    setNotice(apiMessage(error, 'Não foi possível gerar o cupom agora.'));
                }
            })
            .finally(() => setBusyOffer(null));
    };

    // Oferta restrita a planos: avisa antes de tentar quando já sabemos que o plano do cliente não entra.
    const offerBlockedForPlan = (offer) => Boolean(me && offer.planIds?.length > 0 && !offer.planIds.some((id) => me.planIds.includes(id)));

    const handleRedeem = (offer) => {
        if (!token) {
            setPendingOfferId(offer.id);
            setAuthOpen(true);
            return;
        }
        redeem(offer.id);
    };

    const handleAuthenticated = (newToken) => {
        writeSession(tokenKey(companyUuid), newToken);
        setToken(newToken);
        setAuthOpen(false);
        // Quem tocou em "Resgatar" antes de entrar recebe o cupom logo depois da confirmação.
        if (pendingCoupons) {
            setPendingCoupons(false);
            setCouponsOpen(true);
            getClubCoupons(companyUuid, newToken).then(({ data }) => setCoupons(data)).catch(() => setNotice('Não foi possível carregar seus cupons agora.'));
        }
        if (pendingOfferId) {
            const offerId = pendingOfferId;
            setPendingOfferId(null);
            redeem(offerId, newToken);
        }
    };

    const openMyCoupons = () => {
        if (!token) {
            setPendingCoupons(true);
            setAuthOpen(true);
            return;
        }
        setCouponsOpen(true);
        getClubCoupons(companyUuid, token).then(({ data }) => setCoupons(data)).catch((error) => {
            if (error?.response?.status === 401) logout();
        });
    };

    return (
        <div className="club-page public-booking-page" style={{ ...getBrandVars(companyInfo), '--club-accent': accent }}>
            {/* Mesmo topo das demais abas do ambiente do cliente (Agendar, Meus agendamentos, Planos). */}
            <PublicHero
                companyInfo={companyInfo}
                title="Clube de parceiros"
                subtitle={me ? `Olá, ${me.name.split(' ')[0]}. Resgate seus cupons e mostre o código ao parceiro.` : 'Vantagens exclusivas para quem é assinante.'}
            />

            <main className="club-content">
                <div className="club-toolbar">
                    <button type="button" className="club-chip" onClick={openMyCoupons}>
                        <i className="fa-solid fa-ticket"></i> Meus cupons
                    </button>
                </div>
                {notice && <div className="club-notice" role="alert">{notice}</div>}
                {loadError && <div className="club-notice" role="alert">{loadError}</div>}
                {!showcase && !loadError && <div className="club-center"><Spinner animation="border" /></div>}

                {showcase && showcase.categories.length > 0 && (
                    <div className="club-chips" role="tablist">
                        <button type="button" className={`club-chip ${!category ? 'is-active' : ''}`} onClick={() => setCategory('')}>Tudo</button>
                        {showcase.categories.map((name) => (
                            <button type="button" key={name} className={`club-chip ${category === name ? 'is-active' : ''}`} onClick={() => setCategory(name)}>{name}</button>
                        ))}
                    </div>
                )}

                {!category && featured.length > 0 && (
                    <section className="club-featured" aria-label="Em destaque">
                        {featured.map((partner) => (
                            <button type="button" key={partner.id} className="club-featured__item" onClick={() => setSelected(partner)}
                                style={{ backgroundImage: `url(${clubImageUrl(partner.coverPath)})` }}>
                                <span className="club-featured__shade">
                                    <strong>{partner.name}</strong>
                                    {partner.offers[0] && <small>{partner.offers[0].title}</small>}
                                </span>
                            </button>
                        ))}
                    </section>
                )}

                {showcase && partners.length === 0 && !loadError && (
                    <div className="club-empty">
                        <i className="fa-solid fa-handshake"></i>
                        <p>Os parceiros do Clube vão aparecer aqui.</p>
                    </div>
                )}

                <section className="club-grid">
                    {partners.map((partner) => <PartnerCard key={partner.id} partner={partner} onOpen={setSelected} />)}
                </section>
            </main>

            <PublicBrandSignature />
            <ClientTabBar companyUuid={companyUuid} />

            <PartnerModal partner={selected} onHide={() => setSelected(null)} me={me} busyOffer={busyOffer}
                blocked={offerBlockedForPlan} onRedeem={handleRedeem} />
            <CouponModal coupon={coupon} onHide={() => setCoupon(null)} />
            <MyCouponsModal show={couponsOpen} coupons={coupons} onHide={() => setCouponsOpen(false)} onOpen={(item) => { setCouponsOpen(false); setCoupon(item); }} />
            <AuthModal show={authOpen} company={companyUuid} onHide={() => { setAuthOpen(false); setPendingOfferId(null); setPendingCoupons(false); }} onAuthenticated={handleAuthenticated} />
        </div>
    );
};

const PartnerModal = ({ partner, onHide, me, busyOffer, blocked, onRedeem }) => {
    if (!partner) return null;
    const cover = clubImageUrl(partner.coverPath);
    const links = [
        partner.website && { icon: 'fa-solid fa-globe', label: partner.website.replace(/^https?:\/\//i, ''), href: websiteUrl(partner.website) },
        partner.whatsapp && { icon: 'fa-brands fa-whatsapp', label: 'WhatsApp', href: whatsappUrl(partner.whatsapp) },
        partner.instagram && { icon: 'fa-brands fa-instagram', label: profileHandle(partner.instagram), href: instagramUrl(partner.instagram) },
        partner.facebook && { icon: 'fa-brands fa-facebook', label: profileHandle(partner.facebook), href: facebookUrl(partner.facebook) }
    ].filter(Boolean);

    return (
        <Modal show onHide={onHide} centered fullscreen="sm-down" dialogClassName="club-modal">
            <Modal.Body className="p-0">
                <div className="club-detail__cover" style={cover
                    ? { backgroundImage: `url(${cover})` }
                    : { background: `linear-gradient(135deg, ${partner.brandColor || '#fb6a21'}, color-mix(in srgb, ${partner.brandColor || '#fb6a21'} 55%, #000))` }}>
                    <button type="button" className="club-detail__close" onClick={onHide} aria-label="Fechar"><i className="fa-solid fa-xmark"></i></button>
                </div>
                <div className="club-detail__body">
                    <div className="club-detail__logo"><PartnerAvatar partner={partner} size={76} /></div>
                    <h2 className="club-detail__name">{partner.name}</h2>
                    {partner.category && <span className="club-pill">{partner.category}</span>}
                    {partner.description && <p className="club-detail__desc">{partner.description}</p>}
                    {partner.address && (
                        <a className="club-detail__address" target="_blank" rel="noreferrer"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partner.address)}`}>
                            <i className="fa-solid fa-location-dot"></i> {partner.address}
                        </a>
                    )}
                    {links.length > 0 && (
                        <div className="club-detail__links">
                            {links.map((link) => (
                                <a key={link.icon} href={link.href} target="_blank" rel="noreferrer" className="club-chip">
                                    <i className={link.icon}></i> {link.label}
                                </a>
                            ))}
                        </div>
                    )}

                    <h3 className="club-detail__section">Ofertas para assinantes</h3>
                    {partner.offers.length === 0 && <p className="club-muted">Em breve este parceiro terá ofertas por aqui.</p>}
                    {partner.offers.map((offer) => {
                        const unavailable = blocked(offer);
                        return (
                            <div className="club-offer" key={offer.id}>
                                <div className="club-offer__text">
                                    <strong>{offer.title}</strong>
                                    {offer.rules && (
                                        <ul className="club-offer__rules">
                                            {offer.rules.split('\n').filter(Boolean).map((rule) => <li key={rule}>{rule}</li>)}
                                        </ul>
                                    )}
                                    <small>
                                        {[usageLimitLabel(offer), offer.validUntil && `até ${formatDate(offer.validUntil)}`,
                                            offer.planNames?.length > 0 && `Planos: ${offer.planNames.join(', ')}`].filter(Boolean).join(', ')}
                                    </small>
                                </div>
                                <Button className="club-btn" disabled={unavailable || busyOffer === offer.id} onClick={() => onRedeem(offer)}>
                                    {busyOffer === offer.id ? <Spinner size="sm" animation="border" /> : unavailable ? 'Indisponível no seu plano' : 'Resgatar cupom'}
                                </Button>
                            </div>
                        );
                    })}
                    {!me && partner.offers.length > 0 && <p className="club-muted club-detail__hint">Para resgatar, confirme seu acesso com o código enviado ao e-mail cadastrado.</p>}
                </div>
            </Modal.Body>
        </Modal>
    );
};

const CouponModal = ({ coupon, onHide }) => {
    const [copied, setCopied] = useState(false);
    if (!coupon) return null;
    const status = COUPON_STATUS[coupon.status] || COUPON_STATUS.VALID;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(coupon.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Sem permissão de área de transferência: o código continua visível na tela.
        }
    };

    return (
        <Modal show onHide={onHide} centered dialogClassName="club-modal club-modal--small">
            <Modal.Body className="club-coupon">
                <button type="button" className="club-detail__close club-detail__close--dark" onClick={onHide} aria-label="Fechar"><i className="fa-solid fa-xmark"></i></button>
                <span className={`club-status club-status--${status.tone}`}>{status.label}</span>
                <p className="club-coupon__partner">{coupon.partnerName}</p>
                <h2 className="club-coupon__offer">{coupon.offerTitle}</h2>
                <div className="club-coupon__code" aria-label={`Código ${coupon.code}`}>{formatCouponCode(coupon.code)}</div>
                <Button variant="light" className="club-btn club-btn--ghost" onClick={copy}>
                    <i className="fa-regular fa-copy me-1"></i>{copied ? 'Copiado' : 'Copiar código'}
                </Button>
                <p className="club-coupon__hint">
                    {coupon.status === 'USED' ? `Usado em ${formatDateTime(coupon.usedAt)}.`
                        : coupon.status === 'VALID' ? `Mostre este código ao parceiro. Vale até ${formatDateTime(coupon.expiresAt)} e só pode ser usado uma vez.`
                            : 'Este cupom não vale mais.'}
                </p>
            </Modal.Body>
        </Modal>
    );
};

const MyCouponsModal = ({ show, coupons, onHide, onOpen }) => (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down" dialogClassName="club-modal club-modal--small">
        <Modal.Header closeButton><Modal.Title>Meus cupons</Modal.Title></Modal.Header>
        <Modal.Body>
            {coupons.length === 0 && <p className="club-muted">Você ainda não resgatou nenhum cupom.</p>}
            {coupons.map((item) => {
                const status = COUPON_STATUS[item.status] || COUPON_STATUS.VALID;
                return (
                    <button type="button" key={item.id} className="club-coupon-row" onClick={() => onOpen(item)}>
                        <span>
                            <strong>{item.offerTitle}</strong>
                            <small>{item.partnerName}</small>
                        </span>
                        <span className={`club-status club-status--${status.tone}`}>{status.label}</span>
                    </button>
                );
            })}
        </Modal.Body>
    </Modal>
);

/** Usa a identificação do booking e confirma o aparelho com um código por e-mail. */
const AuthModal = ({ show, company, onHide, onAuthenticated }) => {
    const [step, setStep] = useState('document');
    const [document, setDocument] = useState('');
    const autoRequested = useRef(false);
    const [code, setCode] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show) {
            autoRequested.current = false;
            return;
        }
        setStep('document');
        setCode('');
        setError('');
        const bookingDocument = getStoredCustomerDocument(company);
        // CPF do booking identifica, mas não autentica: o código protege os cupons do assinante.
        if (bookingDocument && !autoRequested.current) {
            autoRequested.current = true;
            setDocument(bookingDocument);
            setLoading(true);
            requestClubCode(company, bookingDocument)
                .then(({ data }) => {
                    writeSession(documentKey(company), bookingDocument);
                    setMaskedEmail(data.maskedEmail);
                    setStep('code');
                })
                .catch((err) => setError(apiMessage(err, 'Não foi possível enviar o código agora.')))
                .finally(() => setLoading(false));
        }
    }, [show, company]);

    const sendCode = (event) => {
        event?.preventDefault();
        setLoading(true);
        setError('');
        requestClubCode(company, document)
            .then(({ data }) => {
                writeSession(documentKey(company), document);
                setMaskedEmail(data.maskedEmail);
                setStep('code');
            })
            .catch((err) => setError(apiMessage(err, 'Não foi possível enviar o código agora.')))
            .finally(() => setLoading(false));
    };

    const confirm = (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        verifyClubCode(company, document, code)
            .then(({ data }) => onAuthenticated(data.token))
            .catch((err) => setError(apiMessage(err, 'Código inválido ou vencido.')))
            .finally(() => setLoading(false));
    };

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="club-modal club-modal--small">
            <Modal.Header closeButton><Modal.Title>{step === 'document' ? 'Confirmar acesso' : 'Confirme seu e-mail'}</Modal.Title></Modal.Header>
            <Modal.Body>
                {step === 'document' ? (
                    <div>
                        {loading ? <div className="club-center"><Spinner animation="border" /></div> : error ? (
                            <>
                                <div className="club-notice" role="alert">{error}</div>
                                <Button className="club-btn w-100" onClick={sendCode}>Tentar novamente</Button>
                            </>
                        ) : (
                            <>
                                <p className="club-muted">Identifique-se no agendamento para acessar seus cupons.</p>
                                <Link className="club-btn btn w-100" to={`/booking/${company}`}>Ir para agendamento</Link>
                            </>
                        )}
                    </div>
                ) : (
                    <Form onSubmit={confirm}>
                        <p className="club-muted">Enviamos um código de 6 dígitos para <strong>{maskedEmail}</strong>. Ele vale por 10 minutos.</p>
                        <Form.Control className="club-input club-input--code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
                            value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} autoFocus />
                        {error && <div className="club-notice mt-3" role="alert">{error}</div>}
                        <Button type="submit" className="club-btn w-100 mt-3" disabled={loading || code.length < 6}>
                            {loading ? <Spinner size="sm" animation="border" /> : 'Confirmar'}
                        </Button>
                        <button type="button" className="club-link-button mt-3" onClick={sendCode} disabled={loading}>Não recebi, enviar de novo</button>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ClubPage;
